// State Machine Configuration Loader
// ✅ NO MAGIC STRINGS: Use typed enums and constants

import * as yaml from "js-yaml";

import debugLogger from "../../utils/debugLogger";
import { GatesConfig } from "../GateManager";
import {
  AppState,
  StateEvent,
  StateTransition,
  AppPhase,
  StateMachineConfig,
} from "../types";

export interface StateMachineYamlConfig {
  stateMachine: {
    initialState: string;
    gates?: GatesConfig;
    transitions: Array<{
      from: string;
      event: string;
      to: string;
      gate?: string | null;
      onTransition?: Array<{
        action: string;
        event?: string;
      }>;
    }>;
    phases: Array<{
      state: string;
      description: string;
      dataRequirements: {
        requiredModules: string[];
        requiredData: string[];
        optionalData?: string[];
      };
      allowedRoutes: {
        routes: string[];
        redirectTo?: string;
      };
      initialization?: {
        services?: string[];
        onComplete?: {
          event: string;
        };
        onError?: {
          event: string;
        };
      };
    }>;
  };
}

/**
 * Load and parse state machine configuration from YAML file
 * In Vite, we fetch the file from the public directory
 */
export async function loadStateMachineConfig(configPath?: string): Promise<{
  config: StateMachineConfig;
  gates: GatesConfig;
  initializationServices?: string[];
  transitionGates: Array<{
    from: AppState;
    event: StateEvent;
    gate: string | null;
  }>;
}> {
  const defaultPath = "/config/state-machine.config.yml";
  const filePath = configPath || defaultPath;

  try {
    // Fetch the YAML file
    const response = await fetch(filePath);
    if (!response.ok) {
      throw new Error(`Failed to fetch config file: ${response.statusText}`);
    }
    const fileContents = await response.text();
    const parsed = yaml.load(fileContents) as StateMachineYamlConfig;

    if (!parsed || !parsed.stateMachine) {
      throw new Error(
        "Invalid state machine configuration: missing stateMachine section",
      );
    }

    const smConfig = parsed.stateMachine;

    // Parse initial state
    const initialState = smConfig.initialState as AppState;
    if (!Object.values(AppState).includes(initialState)) {
      throw new Error(`Invalid initial state: ${initialState}`);
    }

    // Parse gates
    const gates: GatesConfig = smConfig.gates || {};

    // Parse transitions (gates will be set separately on StateMachine)
    const transitions: StateTransition[] = smConfig.transitions.map((t) => ({
      from: t.from as AppState,
      event: t.event as StateEvent,
      to: t.to as AppState,
    }));

    // Auto-generate gates for transitions that don't have one
    // Gate name format: {fromState}_to_{toState} or use specified gate
    const transitionGates: Array<{
      from: AppState;
      event: StateEvent;
      gate: string | null;
    }> = smConfig.transitions.map((t) => {
      let gateName: string | null = t.gate || null;

      // If no gate specified, auto-generate one
      if (!gateName) {
        // Generate gate name: {fromState}_to_{toState}
        gateName = `${t.from}_to_${t.to}`;

        // Create gate config if it doesn't exist
        if (!gates[gateName]) {
          gates[gateName] = {
            enabled: true,
            closed: false, // Open by default
            description: `Blocks transition from ${t.from} to ${t.to}`,
          };
        }
      }

      return {
        from: t.from as AppState,
        event: t.event as StateEvent,
        gate: gateName,
      };
    });

    // Parse phases
    const phases: AppPhase[] = smConfig.phases.map((p) => ({
      state: p.state as AppState,
      description: p.description,
      dataRequirements: {
        requiredModules: p.dataRequirements.requiredModules,
        requiredData: p.dataRequirements.requiredData,
        optionalData: p.dataRequirements.optionalData,
      },
      allowedRoutes: {
        routes: p.allowedRoutes.routes,
        redirectTo: p.allowedRoutes.redirectTo,
      },
    }));

    // Get initialization services from INITIALIZATION phase
    const initializationPhase = smConfig.phases.find(
      (p) => p.state === "initialization",
    );
    const initializationServices =
      initializationPhase?.initialization?.services;

    const config: StateMachineConfig = {
      initialState,
      transitions,
      phases,
    };

    return {
      config,
      gates,
      initializationServices,
      transitionGates, // Include gate assignments
    };
  } catch (error) {
    if (error instanceof Error) {
      throw new Error(
        `Failed to load state machine config from ${filePath}: ${error.message}`,
      );
    }
    throw error;
  }
}

/**
 * Synchronous version for environments that support it (Node.js)
 * Falls back to default config in browser
 */
export function loadStateMachineConfigSync(configPath?: string): {
  config: StateMachineConfig;
  gates: GatesConfig;
  initializationServices?: string[];
  transitionGates: Array<{
    from: AppState;
    event: StateEvent;
    gate: string | null;
  }>;
} {
  // In browser environment, return default config
  // Use async loadStateMachineConfig instead
  if (typeof window !== "undefined") {
    debugLogger.warn(
      "loadStateMachineConfigSync called in browser, use loadStateMachineConfig instead",
    );
    // Return minimal default config
    return {
      config: {
        initialState: AppState.INITIALIZATION,
        transitions: [],
        phases: [],
      },
      gates: {},
      transitionGates: [],
    };
  }

  // Node.js environment (for testing/build tools)
  try {
    const fs = require("fs");
    const path = require("path");
    const defaultPath = path.resolve(
      process.cwd(),
      "public",
      "config",
      "state-machine.config.yml",
    );
    const filePath = configPath || defaultPath;
    const fileContents = fs.readFileSync(filePath, "utf8");
    const parsed = yaml.load(fileContents) as StateMachineYamlConfig;

    if (!parsed || !parsed.stateMachine) {
      throw new Error(
        "Invalid state machine configuration: missing stateMachine section",
      );
    }

    const smConfig = parsed.stateMachine;
    const initialState = smConfig.initialState as AppState;
    const gates: GatesConfig = smConfig.gates || {};
    const transitions: StateTransition[] = smConfig.transitions.map((t) => ({
      from: t.from as AppState,
      event: t.event as StateEvent,
      to: t.to as AppState,
    }));

    // Auto-generate gates for transitions that don't have one
    const transitionGates: Array<{
      from: AppState;
      event: StateEvent;
      gate: string | null;
    }> = smConfig.transitions.map((t) => {
      let gateName: string | null = t.gate || null;

      // If no gate specified, auto-generate one
      if (!gateName) {
        gateName = `${t.from}_to_${t.to}`;

        // Create gate config if it doesn't exist
        if (!gates[gateName]) {
          gates[gateName] = {
            enabled: true,
            closed: false,
            description: `Blocks transition from ${t.from} to ${t.to}`,
          };
        }
      }

      return {
        from: t.from as AppState,
        event: t.event as StateEvent,
        gate: gateName,
      };
    });
    const phases: AppPhase[] = smConfig.phases.map((p) => ({
      state: p.state as AppState,
      description: p.description,
      dataRequirements: {
        requiredModules: p.dataRequirements.requiredModules,
        requiredData: p.dataRequirements.requiredData,
        optionalData: p.dataRequirements.optionalData,
      },
      allowedRoutes: {
        routes: p.allowedRoutes.routes,
        redirectTo: p.allowedRoutes.redirectTo,
      },
    }));
    const initializationPhase = smConfig.phases.find(
      (p) => p.state === "initialization",
    );
    const initializationServices =
      initializationPhase?.initialization?.services;

    return {
      config: {
        initialState,
        transitions,
        phases,
      },
      gates,
      initializationServices,
      transitionGates,
    };
  } catch (error) {
    if (error instanceof Error) {
      throw new Error(`Failed to load state machine config: ${error.message}`);
    }
    throw error;
  }
}
